import Core            from '@/core/Core';
import DemandsDAO      from '@/domains/Demand/DemandsDAO';
import Demand          from '@/domains/Demand/Demand';
import ProductsService from '@/domains/Products/ProductsService';
import TendersService  from '@/domains/Tenders/TendersService';
import ContextMenuItem from '@/lib/components/VContextMenu/ContextMenuItem';

import type RpcDataPagination from '@/core/services/Rpc/RpcDataPagination';
import type RpcDataSort       from '@/core/services/Rpc/RpcDataSort';

class DemandsService {

  /**
   * Ищет товары по артикулам покупателя.
   * Возвращает сырые записи бэка: productid / productcode / productname.
   */
  static async findProductsByCodes(codes: string[]): Promise<any[]> {
    const response = await DemandsDAO.findProductsByCodes(codes);
    return Core.utils.isNotEmptyArray(response.data) ? response.data : [];
  }

  /**
   * Загружает список потребностей.
   * Для использования вне реестра (диалоги, автокомплиты).
   */
  static async getList(
    params?: Parameters<typeof DemandsDAO.getList>[0]
  ): Promise<Demand[]> {
    return (await this.getListPaginated(params)).data;
  }

  /**
   * Загружает список потребностей с пагинацией.
   * Используется в реестре через DataGridLoader.
   */
  static async getListPaginated(
    params?: Parameters<typeof DemandsDAO.getList>[0] & {
      pagination?: RpcDataPagination,
      sort?: RpcDataSort
    }
  ): Promise<{ data: Demand[]; pagination: RpcDataPagination }> {
    const { pagination, sort, ...filters } = params ?? {};
    const response = await DemandsDAO.getList(filters, pagination, sort);

    return {
      data:       Core.utils.hydrateArray(response.data, Demand.fromServer) as Demand[],
      pagination: response.meta.pagination,
    };
  }

  /**
   * Загружает одну потребность по ID
   */
  static async get(id: number): Promise<Demand> {
    const response = await DemandsDAO.get(id);
    return Core.utils.hydrate(response.data, Demand.fromServer) as Demand;
  }

  /**
   * Создаёт потребности
   */
  static async add(demands: Demand[]): Promise<boolean> {
    const response = await DemandsDAO.add(demands);

    if (response.ok) {
      Core.showNotification.success('Потребности успешно созданы');
    }

    return response.ok;
  }

  /**
   * Обновляет потребность
   */
  static async update(demand: Demand): Promise<boolean> {
    const response = await DemandsDAO.update(demand);

    if (response.ok) {
      Core.showNotification.success('Потребность успешно обновлена');
    }

    return response.ok;
  }

  /**
   * Удаляет потребность по ID
   */
  static async delete(id: number): Promise<boolean> {
    const response = await DemandsDAO.delete(id);

    if (response.ok) {
      Core.showNotification.success('Потребность успешно удалена');
    }

    return response.ok;
  }

  /**
   * Удаляет потребность с подтверждением. Возвращает true, если удаление произошло —
   * вызывающий сам решает что делать (обычно refresh лоадера реестра).
   */
  static async deleteWithConfirm(demand: Demand): Promise<boolean> {
    const confirmed = await Core.confirm(
      'Подтверждаете удаление потребности?',
      `Потребность: ${demand.product.name}`,
    );

    if (confirmed) {
      return await this.delete(demand.id);
    } else {
      return false;
    }
  }

  /**
   * Массовое удаление потребностей: подтверждение, параллельный delete,
   * сводное уведомление по успешным и упавшим. Возвращает разнесённые списки.
   */
  static async deleteMany(demands: Demand[]): Promise<{ success: Demand[]; failed: Demand[] }> {
    const success: Demand[] = [];
    const failed:  Demand[] = [];

    if (demands.length === 0) return { success, failed };

    const confirmed = await Core.confirm('Вы уверены, что хотите удалить выбранные потребности?');
    if (confirmed === false) return { success, failed };

    const settled = await Promise.allSettled(
      demands.map(_demand => DemandsDAO.delete(_demand.id)),
    );

    settled.forEach((result, index) => {
      const okay = result.status === 'fulfilled' && result.value.ok === true;
      (okay ? success : failed).push(demands[index]);
    });

    if (success.length > 0) {
      Core.showNotification.success(
        `Успешно удалено ${success.length} ${
          Core.utils.regional.ru.pluralize(success.length, 'потребность', 'потребности', 'потребностей')
        }`,
      );
    }

    if (failed.length > 0) {
      const names = failed.map(_demand => _demand.product.name).join(', ');
      Core.showNotification.error(
        `Не удалось удалить ${failed.length} ${
          Core.utils.regional.ru.pluralize(failed.length, 'потребность', 'потребности', 'потребностей')
        }: ${names}`,
      );
    }

    return { success, failed };
  }

  /**
   * Создаёт тендер котировок по потребности (загружает свежую версию перед созданием)
   */
  static async createFlexibleQtyTenderFromDemand(demand: Demand): Promise<void> {
    const fresh = await this.get(demand.id);
    await TendersService.showTenderCard(null, fresh, null, true);
  }

  /**
   * Открывает карточку товара потребности
   */
  static async showProduct(demand: Demand): Promise<void> {
    const product = await ProductsService.get(demand.product.id);
    if (product === null) return;
    Core.dialogs.ProductCard.show({ product });
  }

  /**
   * Seller workflow: загружает потребность, кладёт её в store как "потребность для
   * создания предложения" и переходит на страницу редактирования предложения
   * нужной товарной категории. Используется в DemandsRegistry в seller-режиме.
   */
  static async createOfferFromDemand(demandId: number): Promise<void> {
    const demand = await this.get(demandId);
    if (!demand) return;

    const { default: store } = await import('@/store');
    await store.dispatch('demand/setDemandForCreate', demand);
    await Core.router.push(`/offers/edit/${demand.product.category.id}`);
  }

  /**
   * Открывает диалог массового создания потребностей (на заказ или предзаказ)
   */
  static showOrderCreateDialog(isPreorder: boolean, onSaved: () => void): void {
    Core.dialogs.DemandOrderCreateDialog.show(
      { isPreorder },
      { save: onSaved },
    );
  }

  /**
   * Открывает карточку потребности.
   * Промис резолвится по закрытию карточки — вызывающий сам решает, рефрешить ли
   * список (как в реестре по двойному клику и в пункте «Открыть» контекстного меню).
   */
  static showDemandCard(demand: Demand | null): Promise<boolean> {
    return Core.dialogs.DemandCard.showAsPromise({ id: demand?.id });
  }

  // ============================================================
  // Контекстное меню
  // ============================================================

  /**
   * Пункты контекстного меню потребности для грида реестра.
   * Передаётся в VDataGridV2 пропом :context-menu — грид сам рисует
   * кебаб-колонку и меню по правому клику, и обновляет список после
   * действия (если у пункта refreshAfter !== false).
   *
   * callback возвращает промис — грид дождётся завершения перед refresh.
   */
  static get contextMenu(): ContextMenuItem[] {
    return [
      new ContextMenuItem(
        'Открыть',
        'open_in_new',
        (demand: Demand) => { return this.showDemandCard(demand); },
        null,
        false,
        true,
        true,
      ),
      new ContextMenuItem(
        'Сравнить предложения',
        'difference',
        (demand: Demand) => Core.dialogs.OrderOptionsComparisonDialog.showAsPromise({ demandId: demand.id }),
        (demand: Demand) => demand.getReasonCannotCompareOffers(),
        () => Core.currentPartner?.isBuyer === false,
      ),
      new ContextMenuItem(
        'Создать предложение',
        'add_circle_outline',
        (demand: Demand) => { return this.createOfferFromDemand(demand.id); },
        null,
        () => Core.currentPartner?.isSeller === false,
        false,
      ),
      new ContextMenuItem(
        'Создать тендер',
        'gavel',
        (demand: Demand) => {
          return Core.dialogs.TenderCreateCard.showAsPromise({
            demand: demand,
            isFlexible: false
          });
        },
        (demand: Demand) => demand.getReasonCannotCreateTender(),
      ),
      new ContextMenuItem(
        'Создать тендер котировок',
        'gavel',
        (demand: Demand) => {
          return Core.dialogs.TenderCreateCard.showAsPromise({
            demand: demand,
            isFlexible: true
          });
        },
        (demand: Demand) => demand.getReasonCannotCreateTender(),
      ),
      new ContextMenuItem(
        'Открыть тендер',
        'open_in_new',
        (demand: Demand) => { TendersService.showTenderCard(demand.tender); },
        (demand: Demand) => demand.getReasonCannotShowTender(),
        false,
        false,
      ),
      new ContextMenuItem(
        'Открыть товар',
        'open_in_new',
        (demand: Demand) => { return this.showProduct(demand); },
        null,
        false,
        false,
      ),
      new ContextMenuItem(
        'История потребности',
        'history',
        (demand: Demand) => { Core.dialogs.FeedDialog.show({ entityTypeName: 'demand', entityId: demand.id, showAddComment: false }); },
        null,
        false,
        false,
        true,
      ),
      new ContextMenuItem(
        'Удалить',
        'delete',
        (demand: Demand) => { return this.deleteWithConfirm(demand); },
        (demand: Demand) => demand.getReasonCannotDelete(),
      ),
    ];
  }
}

export default DemandsService;
